import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGenerationCosts } from '../hooks/useGenerationCosts';
import { ImageSelector } from '../components/ImageSelector';
import { AllGenerationsViewer } from '../components/AllGenerationsViewer';
import { GenerationLoadingOverlay, LOADING_SEQUENCES } from '../components/GenerationLoadingOverlay';
import { CloudflareR2Service } from '../lib/cloudflareR2Service';
import ReactBeforeSliderComponent from 'react-before-after-slider-component';
import 'react-before-after-slider-component/dist/build.css';
import { OptimizedImage, ImageOptimizer } from '../lib/imageOptimizer';
import { tokenService } from '../lib/tokenService';
import { userImageService, UserImage } from '../lib/userImageService';
import { userGalleryService } from '../lib/userGalleryService';
import { furnitureRemovalService } from '../lib/furnitureRemovalService';

const cleanupBlobUrl = (url: string | null) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

export const AIRemoveFurniture: React.FC = () => {
  const { user, checkTokenBalance, refreshTokenBalance, updateUserTokens, isUpdatingTokens } = useAuth();
  const { costs } = useGenerationCosts();
  const resultSectionRef = useRef<HTMLDivElement>(null);
  const operationSectionRef = useRef<HTMLDivElement>(null);
  const isSubmittingRef = useRef(false);
  
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [optimizedImage, setOptimizedImage] = useState<OptimizedImage | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<React.ReactNode | null>(null);
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [lastProcessingId, setLastProcessingId] = useState<string>('');
  
  // Get dynamic token cost for AI furniture removal
  const TOKENS_REQUIRED = costs.ai_furniture_removal || 8; // Fallback to 8 if not loaded yet

  const currentTokenBalance = useMemo(() => user?.tokens || 0, [user?.tokens]);

  const handleImageSelected = async (file: File, source: 'upload' | 'existing', imageData?: UserImage) => {
    // Reset state
    setError(null);
    setSuccess(false);
    setOptimizedImage(null);
    cleanupBlobUrl(resultImage);
    setResultImage(null);
    setLastProcessingId('');

    setSelectedImage(file);
    setLoading(true);

    try {
      // Handle CORS workaround files
      let actualFile = file;
      if ((file as any).isCorsWorkaround) {
        console.log('🔄 Detected CORS workaround file, getting actual image data...');
        actualFile = await userImageService.getImageDataForWorkaround(file);
      }

      const isHeicFile = actualFile.name.toLowerCase().endsWith('.heic') || actualFile.name.toLowerCase().endsWith('.heif');

      if (isHeicFile) {
        try {
          const r2Service = CloudflareR2Service.fromEnvironment();
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const uploadResult = await r2Service.uploadRawFileForProcessing(actualFile, { userId: user?.id });

          // HERE: Trigger backend processing and wait for the result
          // This is a placeholder for the actual server-side call
          // const convertedImageUrl = await triggerBackendConversion(uploadResult.key);

          // For now, we will simulate a delay and then show an error
          await new Promise(resolve => setTimeout(resolve, 5000));
          throw new Error("Server-side conversion is not yet implemented.");

        } catch (uploadError) {
          console.error('HEIC upload failed:', uploadError);
          setError('Failed to upload HEIC file for conversion. Please try again.');
          setLoading(false);
          return;
        }
      }

      // Optimize the image
      const optimized = await ImageOptimizer.optimizeImage(actualFile);
      setOptimizedImage(optimized);
      
      // Create preview URL from optimized image
      const reader = new FileReader();
      reader.onload = (e) => setImagePreviewUrl(e.target?.result as string);
      reader.readAsDataURL(optimized.file);

      // For new uploads, also save to user gallery
      if (source === 'upload' && user?.id) {
        try {
          await userGalleryService.uploadUserImage(actualFile, user.id);
        } catch (galleryError) {
          console.warn('Failed to save to gallery:', galleryError);
          // Continue with AI furniture removal even if gallery save fails
        }
      }

      setLoading(false);
      
      // Scroll to operations section if an existing image was selected
      if (source === 'existing' && operationSectionRef.current) {
        setTimeout(() => {
          operationSectionRef.current?.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }, 300); // Small delay to ensure image is loaded and UI is updated
      }
    } catch (error) {
      console.error('Image processing failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to process image';

      if (errorMessage.startsWith('HEIC_CONVERSION_FAILED')) {
        setError(
          <span>
            HEIC/HEIF conversion is not supported in your browser. Please{' '}
            <a href="https://cloudconvert.com/heic-to-jpg" target="_blank" rel="noopener noreferrer" className="underline text-blue-600">
              convert your image to JPG
            </a>{' '}
            or try a different browser.
          </span>
        );
      } else {
        setError(errorMessage);
      }
      
      // Fallback to original file for unsupported formats
      const reader = new FileReader();
      reader.onload = (e) => setImagePreviewUrl(e.target?.result as string);
      reader.readAsDataURL(file);
      setLoading(false);
    }
  };

  const handleImageRemoved = () => {
    setSelectedImage(null);
    setOptimizedImage(null);
    cleanupBlobUrl(imagePreviewUrl);
    setImagePreviewUrl(null);
    setError(null);
  };

  useEffect(() => {
    return () => cleanupBlobUrl(resultImage);
  }, [resultImage]);

  // Scroll to result section when result image is available
  useEffect(() => {
    if (resultImage && resultSectionRef.current) {
      resultSectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [resultImage]);


  const handleSubmit = async () => {
    if (isSubmittingRef.current || !selectedImage || !optimizedImage || !user?.id) {
      setError('Please upload an image first.');
      return;
    }

    isSubmittingRef.current = true;
    setLoading(true);
    setError(null);
    
    const processingId = `ai-furniture-removal-${Date.now()}`;
    setLastProcessingId(processingId);

    try {
      await checkTokenBalance(user);
      if ((user?.tokens || 0) < TOKENS_REQUIRED) {
        setError(`Not enough tokens. You need ${TOKENS_REQUIRED}.`);
        return;
      }

      // Upload optimized image to R2
      const r2Service = CloudflareR2Service.fromEnvironment();
      const uploadResult = await r2Service.uploadOptimizedImage(
        optimizedImage.file,
        processingId,
        user.id,
        'furniture_removal'
      );

      // Create JSON payload for clear_all operation
      const payload = {
        operation_type: 'clear_all' as const,
        processingId: processingId,
        originalImageUrl: uploadResult.url,
        userId: user?.id
      };

      const webhookUrl = process.env.REACT_APP_N8N_WEBHOOK_URL_FURNITURE_REMOVAL;
      
      if (!webhookUrl) {
        throw new Error('Furniture removal webhook URL not configured.');
      }

      // Save operation to database
      const operationData = {
        processing_id: processingId,
        operation_type: 'clear_all' as const,
        original_image_url: uploadResult.url,
        tokens_spent: TOKENS_REQUIRED,
        webhook_url: webhookUrl
      };

      await furnitureRemovalService.createOperation(user.id, operationData);

      if (!await tokenService.spendTokens(user.id, TOKENS_REQUIRED, 'AI Furniture Removal')) {
        throw new Error('Failed to spend tokens.');
      }
      updateUserTokens((user?.tokens || 0) - TOKENS_REQUIRED);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const response = await fetch(webhookUrl, { 
        method: 'POST', 
        mode: 'cors', 
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload), 
        signal: controller.signal 
      });
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`Webhook failed: ${response.statusText}`);
      
      const result = await response.json();
      const imageUrl = result[0]?.response?.body[0]?.fullUrl || result[0]?.fullUrl || result.image || result.fullUrl;

      if(imageUrl){
        // Mark operation as completed
        await furnitureRemovalService.markOperationCompleted(processingId, imageUrl, result);
        
        setResultImage(imageUrl);
        setSuccess(true);
      } else {
        // Mark operation as failed
        await furnitureRemovalService.markOperationFailed(processingId, "No image was returned from the AI service");
        setError("AI furniture removal finished but no image was returned.");
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred.';
      setError(errorMessage);
      
      // Mark operation as failed if we have a processing ID
      if (lastProcessingId) {
        try {
          await furnitureRemovalService.markOperationFailed(lastProcessingId, errorMessage);
        } catch (dbError) {
          console.error('Failed to mark operation as failed:', dbError);
        }
      }
    } finally {
      setLoading(false);
      isSubmittingRef.current = false;
      // Refresh token balance after operation to show updated balance
      refreshTokenBalance(user);
    }
  };

  return (
    <div className="max-w-6xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">🪑 AI Remove Furniture</h1>
          <p className="text-gray-600">Clear all furniture from your room photos with AI - Perfect for empty room visualizations</p>
          <div className="mt-4 flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="inline-flex items-center bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
              💰 Available: {currentTokenBalance} tokens | Required: {TOKENS_REQUIRED}
            </div>
            <button onClick={() => refreshTokenBalance(user)} disabled={isUpdatingTokens} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-medium">
              {isUpdatingTokens ? 'Refreshing...' : '🔄 Refresh'}
            </button>
          </div>
        </div>

        {error && <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md"><p className="text-red-700">{error}</p></div>}
        {success && <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md"><p className="text-green-700">🎉 AI furniture removal complete!</p></div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <ImageSelector
                title="1. Select Image"
                uploadButtonText="Upload New Image"
                onImageSelected={handleImageSelected}
                onImageRemoved={handleImageRemoved}
                selectedImage={selectedImage}
                acceptedFormats="image/jpeg,image/jpg,image/png,image/heic,image/heif,.jpg,.jpeg,.png,.heic,.heif"
                maxSizeMB={50}
                showExisting={true}
              />
              {imagePreviewUrl && <div className="mt-4"><img src={imagePreviewUrl} alt="Preview" className="w-full h-auto max-h-96 object-contain"/></div>}
            </div>
          </div>
          <div className="space-y-6">
            <div ref={operationSectionRef} className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">2. Furniture Removal</h3>
              <div className="p-4 border-2 border-red-500 bg-red-50 rounded-lg">
                <div className="font-medium">🪑 Clear All Furniture</div>
                <div className="text-sm text-gray-600 mt-1">
                  AI will remove all furniture from the entire room automatically. 
                  Perfect for creating empty rooms or preparing for new furniture layouts.
                </div>
              </div>
              
              <div className="mt-6">
                <button 
                  type="button" 
                  onClick={handleSubmit} 
                  disabled={loading || !optimizedImage || currentTokenBalance < TOKENS_REQUIRED} 
                  className="w-full py-3 px-4 rounded-md text-white bg-blue-600 disabled:bg-gray-400"
                >
                  {loading ? 'Processing...' : `Remove Furniture (${TOKENS_REQUIRED} tokens)`}
                </button>
              </div>
            </div>
          </div>
          
          {resultImage && imagePreviewUrl && (
            <div ref={resultSectionRef} className="md:col-span-2 mt-8">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">🎉 Before & After Comparison</h3>
                <div className="rounded-lg overflow-hidden border border-gray-200">
                  <ReactBeforeSliderComponent
                    firstImage={{
                      imageUrl: imagePreviewUrl,
                      alt: 'Original Image'
                    }}
                    secondImage={{
                      imageUrl: resultImage,
                      alt: 'AI Furniture Removal Result'
                    }}
                    delimiterColor="#3B82F6"
                    currentPercentPosition={50}
                    withResizeFeel={true}
                    feelsOnlyTheDelimiter={false}
                    delimiterIconStyles={{
                      width: '50px',
                      height: '50px',
                      backgroundSize: 'contain',
                      borderRadius: '50%',
                      backgroundImage: 'url("/assets/renovirai-logo.png")',
                      backgroundColor: 'white',
                      border: '2px solid #3B82F6'
                    }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Drag the slider to compare before and after
                </p>
                <div className="mt-6 text-center">
                  <a href={resultImage} download={`${lastProcessingId}.webp`} className="bg-green-600 text-white px-6 py-3 rounded-md">
                    Download Result
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>
        <AllGenerationsViewer />
      </div>
      
      <GenerationLoadingOverlay
        isVisible={loading}
        loadingSteps={LOADING_SEQUENCES.AI_TOUCHUP} // Reuse the same loading sequence
      />
    </div>
  );
};
